import React from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { Plus } from "lucide-react";
import Loader from "@/shared/components/ui/Loaders/Loader";
import StepConditionModal from "./Sequence/StepConditionModal";
import ConditionCard from "./components/ConditionCard";
import ConditionNode from "./components/ConditionNode";
import SortableStepItem from "./components/SortableStepItem";
import ConditionWithBranches from "./components/ConditionWithBranches";
import { ConnectorLine } from "./components/ConnectorLine";
import StartNode from "./components/StartNode";
import DeleteConfirmationModal from "@/shared/components/ui/Modal/DeleteConfirmationModal";
import { useCustomWorkflowBuilder } from "./hooks/useCustomWorkflowBuilder";
import { useMainStore } from "@/stores/index";
import CustomCommonTooltip from "@/shared/components/ui/Upload/ToolTip/CustomCommonTooltip";

const AddStepButton = ({ onClick, disabled, title }) => (
  <div>
    {disabled ? (
      <CustomCommonTooltip content="Pause the campaign to add a new step.">
        <button
          type="button"
          onClick={onClick}
          disabled={disabled}
          title={title}
          className="flex items-center gap-1 px-3.5 py-2.5 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-800 rounded-lg shadow-sm hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors text-[0.715rem] font-inter text-gray-700 dark:text-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Plus size={14} className="text-gray-500" strokeWidth={2} />
          Add Condition/Step
        </button>
      </CustomCommonTooltip>
    ) : (
      <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        title={title}
        className="flex items-center gap-1 px-3.5 py-2.5 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-800 rounded-lg shadow-sm hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors text-[0.715rem] font-inter text-gray-700 dark:text-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <Plus size={14} className="text-gray-500" strokeWidth={2} />
        Add Condition/Step
      </button>
    )}
  </div>
);

// const ModeToggle = ({ onModeChange, isSmartMode }) => (
//   <div className="flex items-center gap-2.5 text-[0.715rem] font-inter text-gray-700 bg-white border border-gray-200 rounded-lg p-2.5">
//     <span className="text-nowrap">Static Mode</span>
//     <Switch
//       isSelected={isSmartMode}
//       color="secondary"
//       size="sm"
//       classNames={{
//         base: "group-data-[selected=true]:bg-secondary",
//         wrapper: "group-data-[selected=false]:!bg-[#535862]",
//       }}
//       onValueChange={onModeChange}
//       thumbIcon={({ isSelected, className }) =>
//         isSelected ? (
//           <Zap className={className} size={11} fill="#207C97" color="#207C97" />
//         ) : (
//           <Wrench
//             className={className}
//             size={10}
//             fill="#535862"
//             color="#535862"
//           />
//         )
//       }
//     />
//     <span className="text-nowrap">Smart Mode</span>
//   </div>
// );

const CustomWorkflowBuilder = () => {
  const {
    items,
    steps,
    isLoading,
    activeItem,
    showAddModal,
    showDeleteConfirmModal,
    editingStep,
    editingCondition,
    deletingStep,
    deletingCondition,
    isCreatingStep,
    isUpdating,
    isDeletingStep,
    handleDragStart,
    handleDragEnd,
    handleEdit,
    handleDelete,
    handleSaveStep,
    handleConfirmDelete,
    handleAddStep,
    handleAddBranchStep,
    branchCondition,
    closeAddModal,
    closeDeleteModal,
    handleModeChange: _handleModeChange,
    isCampaignRunning,
  } = useCustomWorkflowBuilder();
  const workflowMode = useMainStore((state) => state.workflowMode);
  const _isSmartMode = workflowMode === "smart";
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  if (isLoading) {
    return (
      <div className="h-[calc(100dvh-200px)] w-full flex items-center justify-center">
        <Loader text="Loading campaign steps..." />
      </div>
    );
  }

  return (
    <div
      className="h-[calc(100dvh-113px)] w-full   bg-[#F3F3F399] dark:bg-gray-900/90 workflow-dotted-bg relative"
    >
      {/* <div className="absolute top-4 left-4 right-4 z-10 flex items-center justify-end">
        <ModeToggle onModeChange={handleModeChange} isSmartMode={isSmartMode} />
      </div> */}

      <div className="pt-20 pb-8 px-5 h-full overflow-y-auto scrollbar-hide">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={items.map((item) => item.id)}
            strategy={verticalListSortingStrategy}
          >
            <div className="flex flex-col items-center justify-center w-full gap-0">
              <StartNode />

              {items.length === 0 ? (
                <>
                  <ConnectorLine />
                  <AddStepButton
                    onClick={() => handleAddStep(null)}
                    disabled={isCampaignRunning}
                    title={
                      isCampaignRunning
                        ? "Pause the campaign to add a new step."
                        : undefined
                    }
                  />
                </>
              ) : (
                items.map((item, index) => {
                  const isCondition = item.conditions !== null;
                  return (
                    <React.Fragment key={item.id}>
                      <ConnectorLine />
                      {isCondition ? (
                        <ConditionWithBranches
                          condition={item}
                          stepOrder={item.step_order}
                          steps={items}
                          onEdit={handleEdit}
                          onDelete={handleDelete}
                          onAddBranchStep={handleAddBranchStep}
                        />
                      ) : (
                        <SortableStepItem
                          item={item}
                          onEdit={handleEdit}
                          onDelete={handleDelete}
                        />
                      )}
                      <ConnectorLine />
                      <AddStepButton
                        onClick={() => handleAddStep(index)}
                        disabled={isCampaignRunning}
                        title={
                          isCampaignRunning
                            ? "Pause the campaign to add a new step."
                            : undefined
                        }
                      />
                    </React.Fragment>
                  );
                })
              )}
            </div>
          </SortableContext>

          <DragOverlay>
            {activeItem ? (
              <div className="opacity-50 w-full flex justify-center">
                {activeItem.conditions !== null ? (
                  <ConditionCard
                    condition={activeItem}
                    stepOrder={activeItem.step_order}
                  />
                ) : (
                  <ConditionNode data={activeItem} />
                )}
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>
      </div>

      {showAddModal && (
        <StepConditionModal
          isOpen={showAddModal}
          onClose={closeAddModal}
          data={steps}
          onSave={handleSaveStep}
          step={editingStep}
          condition={editingCondition}
          branchMode={branchCondition ? "no" : null}
          isCreatingStep={isCreatingStep}
          isUpdatingStep={isUpdating}
        />
      )}

      {showDeleteConfirmModal && (
        <DeleteConfirmationModal
          showConfirmDelete={showDeleteConfirmModal}
          onClose={closeDeleteModal}
          title={`Are you sure you want to delete ${deletingStep ? `step ${deletingStep?.step_order}` : `condition`
            } ?`}
          description={`Are you sure you want to delete ${deletingStep
            ? `step ${deletingStep?.step_order}`
            : `condition "${deletingCondition?.conditions}"`
            } ? This action cannot be undone.`}
          onDelete={handleConfirmDelete}
          deleteButtonText={`Yes, Delete ${deletingStep ? "Step" : "Condition"}`}
          cancelButtonText="Cancel"
          isDeleting={isDeletingStep}
          width="430px"
        />
      )}
    </div>
  );
};

export default CustomWorkflowBuilder;
